import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ogImage } from '../../lib/ogImage';
import { Instagram, ChevronRight } from 'lucide-react';
import BlogLayout from '../../components/BlogLayout';

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    { "@type": "Question", name: "Πότε χρειάζομαι λέιζερ πριν το cover up;", acceptedAnswer: { "@type": "Answer", text: "Όταν το παλιό tattoo είναι πολύ σκούρο, γεμάτο ή έχει αρκετό χρώμα. Μερικές συνεδρίες λέιζερ μπορούν να ανοίξουν αρκετά το δέρμα ώστε το cover up να έχει πολύ περισσότερες επιλογές σχεδίου." } },
    { "@type": "Question", name: "Είναι καλύτερο το λέιζερ ή το cover up;", acceptedAnswer: { "@type": "Answer", text: "Εξαρτάται από τι θέλεις να πετύχεις. Αν θες να απαλλαγείς τελείως, λέιζερ. Αν θες νέο τατουάζ στο ίδιο σημείο, cover up ή συνδυασμός. Δεν υπάρχει μία σωστή απάντηση για όλους." } },
    { "@type": "Question", name: "Μπορεί ένα cover up να καλύψει τελείως το παλιό tattoo;", acceptedAnswer: { "@type": "Answer", text: "Σε πολλές περιπτώσεις ναι, αν το design είναι σωστά σχεδιασμένο. Σε άλλες, χρειάζεται fading με λέιζερ πρώτα. Ένα consultation δίνει σαφή εικόνα για κάθε περίπτωση." } },
  ],
};

export default function LaserVsCoverUpAthens() {
  useEffect(() => {
    const inject = (id: string, s: object) => {
      let tag = document.getElementById(id);
      if (!tag) { tag = document.createElement('script'); tag.id = id; (tag as HTMLScriptElement).type = 'application/ld+json'; document.head.appendChild(tag); }
      tag.textContent = JSON.stringify(s);
    };
    const articleSchema = {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "headline": "Λέιζερ ή Cover Up; Τι να Επιλέξεις για Παλιό Τατουάζ",
      "description": "Πότε αξίζει λέιζερ αφαίρεση τατουάζ και πότε cover up, ποιες οι διαφορές, το κόστος, η διαδικασία και πώς να πάρεις τη σωστή απόφαση.",
      "image": "https://210tattoo.gr/og-image.png",
      "datePublished": "2026-04-25",
      "dateModified": "2026-04-25",
      "author": { "@type": "Organization", "name": "210 Tattoo Athens", "url": "https://210tattoo.gr" },
      "publisher": { "@id": "https://210tattoo.gr/#tattooparlor" },
      "url": "https://210tattoo.gr/blog/laser-vs-cover-up-athens",
      "inLanguage": "el"
    };
    inject('schema-laser-cover-faq', faqSchema);
    inject('schema-laser-cover-article', articleSchema);
    return () => {
      document.getElementById('schema-laser-cover-faq')?.remove();
      document.getElementById('schema-laser-cover-article')?.remove();
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>Λέιζερ ή Cover Up; Τι να Επιλέξεις για Παλιό Τατουάζ | 210 Tattoo Athens</title>
        <meta name="description" content="Πότε αξίζει λέιζερ αφαίρεση τατουάζ και πότε cover up, ποιες οι διαφορές, η διαδικασία και πώς να πάρεις τη σωστή απόφαση." />
        <link rel="canonical" href="https://210tattoo.gr/blog/laser-vs-cover-up-athens" />
        <link rel="alternate" hrefLang="el" href="https://210tattoo.gr/blog/laser-vs-cover-up-athens" />
        <link rel="alternate" hrefLang="x-default" href="https://210tattoo.gr/blog/laser-vs-cover-up-athens" />
        <meta property="og:title" content="Λέιζερ ή Cover Up; Τι να Επιλέξεις για Παλιό Τατουάζ | 210 Tattoo Athens" />
        <meta property="og:description" content="Λέιζερ vs cover up: ποιο να επιλέξεις, πότε χρειάζεται συνδυασμός και πώς λειτουργεί κάθε επιλογή." />
        <meta property="og:url" content="https://210tattoo.gr/blog/laser-vs-cover-up-athens" />
        <meta property="og:type" content="article" />
        <meta property="og:image" content={ogImage('laser-vs-cover-up-athens')} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Λέιζερ ή Cover Up; | 210 Tattoo Athens" />
        <meta name="twitter:description" content="Ποιο να επιλέξεις για παλιό τατουάζ που δεν θέλεις πια." />
        <meta name="twitter:image" content={ogImage('laser-vs-cover-up-athens')} />
      </Helmet>

      <BlogLayout>
        {/* Hero */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-pink-950/20 via-black to-black pointer-events-none" />
          <div className="max-w-4xl mx-auto relative z-10">
            <div className="flex items-center gap-2 text-pink-500 text-sm font-medium mb-6">
              <a href="/" className="hover:text-pink-400 transition-colors">Home</a>
              <ChevronRight size={14} />
              <span>Λέιζερ ή Cover Up</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              <span className="bg-gradient-to-r from-pink-500 via-pink-400 to-pink-300 bg-clip-text text-transparent">Λέιζερ ή cover up;</span>
              <br />
              <span className="text-white">Τι να επιλέξεις στην Αθήνα</span>
            </h1>
            <p className="text-gray-500 text-xs mb-4 tracking-widest uppercase">25 Απριλίου 2026</p>
            <p className="text-xl text-gray-300 mb-10 leading-relaxed max-w-2xl">
              Έχεις ένα τατουάζ που δεν θέλεις πια. Η επόμενη ερώτηση είναι: λέιζερ, cover up, ή κάτι ανάμεσα; Η απάντηση εξαρτάται από το τι θέλεις να καταλήξεις να έχεις.
            </p>
            <a href="https://ig.me/m/210tattoo" target="_blank" rel="noopener noreferrer" className="bg-gradient-to-r from-pink-600 to-pink-700 hover:from-pink-700 hover:to-pink-800 px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-300 inline-flex items-center gap-3 shadow-2xl hover:shadow-pink-500/30 transform hover:scale-105">
              <Instagram size={20} />
              Κλείσε Ραντεβού
            </a>
          </div>
        </section>

        {/* What each option is */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Τι είναι το καθένα, <span className="text-pink-500">και τι κάνει</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-8">
              Πριν συγκρίνεις, αξίζει να ξέρεις ακριβώς τι κάνει κάθε επιλογή. Και τα δύο έχουν νόημα, σε διαφορετικές περιπτώσεις.
            </p>
            <div className="grid sm:grid-cols-2 gap-4 mb-8">
              {[
                { title: 'Λέιζερ αφαίρεση', desc: 'Χρησιμοποιεί παλμικό φως για να διασπάσει τη μελάνη κάτω από το δέρμα. Απαιτεί πολλές συνεδρίες και το αποτέλεσμα εξαρτάται από το χρώμα, το βάθος και την ηλικία του tattoo.' },
                { title: 'Cover up tattoo', desc: 'Νέο tattoo που σχεδιάζεται για να καλύψει το παλιό. Δεν το αφαιρεί, το κρύβει κάτω από νέο design που έχει μελετηθεί ειδικά για αυτό.' },
                { title: 'Fading πριν cover up', desc: 'Μερικές συνεδρίες λέιζερ που δεν αφαιρούν τελείως αλλά ανοίγουν αρκετά το τατουάζ ώστε το cover up να έχει πολύ μεγαλύτερη ελευθερία σχεδίου.' },
                { title: 'Πλήρης αφαίρεση', desc: 'Στόχος είναι δέρμα χωρίς ίχνος. Απαιτεί περισσότερες συνεδρίες λέιζερ, περισσότερο χρόνο και κόστος, αλλά δίνει τελείως καθαρό αποτέλεσμα.' },
              ].map((item, i) => (
                <div key={i} className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200">
                  <h3 className="text-white font-semibold mb-1">{item.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* When cover up works */}
        <section className="py-16 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Πότε το cover up <span className="text-pink-500">είναι η σωστή επιλογή</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-8">
              Το cover up λειτουργεί καλύτερα όταν θέλεις να αντικαταστήσεις το παλιό tattoo με κάτι νέο, και είσαι ανοιχτός στο να προσαρμοστεί το σχέδιο στις απαιτήσεις της κάλυψης.
            </p>
            <div className="space-y-4">
              {[
                { title: 'Το παλιό tattoo δεν είναι υπερβολικά σκούρο', desc: 'Tattoos με ανοιχτές αποχρώσεις, ξεθωριασμένα ή παλιά μαύρα καλύπτονται πολύ πιο εύκολα από φρέσκα και πυκνά.' },
                { title: 'Θέλεις νέο τατουάζ στο ίδιο σημείο', desc: 'Αν ο στόχος είναι να έχεις νέο design εκεί, το cover up, με ή χωρίς fading, είναι η πιο αποτελεσματική επιλογή.' },
                { title: 'Έχεις ευελιξία στο design', desc: 'Ένα καλό cover up χρειάζεται ένα σχέδιο που «δουλεύει» με το παλιό. Αν είσαι δεκτικός σε αυτό, τα αποτελέσματα μπορεί να είναι εντυπωσιακά.' },
                { title: 'Δεν θέλεις να περάσεις από πολλές συνεδρίες λέιζερ', desc: 'Ένα cover up με ή χωρίς ελάχιστο fading είναι συνήθως γρηγορότερη και λιγότερο επώδυνη διαδρομή από πλήρη αφαίρεση.' },
              ].map((item, i) => (
                <div key={i} className="flex gap-4 items-start">
                  <span className="w-2 h-2 rounded-full bg-pink-500 shrink-0 mt-2" />
                  <div>
                    <p className="text-white font-semibold mb-1">{item.title}</p>
                    <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* When laser is needed */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Πότε χρειάζεσαι <span className="text-pink-500">λέιζερ</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-8">
              Υπάρχουν περιπτώσεις όπου το λέιζερ είναι αναγκαίο, είτε ως βήμα πριν το cover up, είτε ως η μόνη επιλογή.
            </p>
            <div className="grid sm:grid-cols-3 gap-4 mb-8">
              {[
                { title: 'Πυκνό, σκούρο tattoo', desc: 'Αν είναι γεμάτο μαύρο ή σκούρες αποχρώσεις, το cover up χωρίς fading έχει πολύ περιορισμένες επιλογές σχεδίου.' },
                { title: 'Θέλεις καθαρό δέρμα', desc: 'Αν ο στόχος είναι να μην υπάρχει τίποτα εκεί, κανένα νέο τατουάζ, τότε μόνο λέιζερ το πετυχαίνει.' },
                { title: 'Δύσκολο placement για cover up', desc: 'Σε περιοχές όπου το νέο τατουάζ πρέπει να είναι πολύ ελαφρύ ή μικρό, το fading ανοίγει δυνατότητες.' },
              ].map((item, i) => (
                <div key={i} className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200">
                  <h3 className="text-white font-semibold mb-2">{item.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Cost and time comparison */}
        <section className="py-16 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Κόστος και χρόνος, <span className="text-pink-500">μια ρεαλιστική εικόνα</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-8">
              Δεν υπάρχει ενιαία τιμή για κανένα από τα δύο. Εξαρτάται από μέγεθος, χρώματα, αριθμό συνεδριών και τον πάροχο. Αλλά υπάρχουν γενικές αρχές που ισχύουν.
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { title: 'Cover up: μία συνεδρία', desc: 'Συνήθως ένα cover up γίνεται σε μία ή δύο συνεδρίες. Αν συνδυαστεί με fading, προσθέτεις 2–6 μήνες προετοιμασίας.' },
                { title: 'Λέιζερ: πολλές συνεδρίες', desc: 'Πλήρης αφαίρεση συνήθως απαιτεί 6–12+ συνεδρίες με διαστήματα 6–8 εβδομάδων. Είναι μεγαλύτερη δέσμευση σε χρόνο.' },
                { title: 'Κόστος cover up', desc: 'Παρόμοιο με ένα συνηθισμένο tattoo αντίστοιχου μεγέθους, συχνά ελαφρώς υψηλότερο λόγω της πολυπλοκότητας του σχεδιασμού.' },
                { title: 'Κόστος λέιζερ', desc: 'Το συνολικό κόστος πλήρους αφαίρεσης ξεπερνά συχνά το κόστος ενός νέου tattoo, και απαιτεί πολύ μεγαλύτερο χρονικό ορίζοντα.' },
              ].map((item, i) => (
                <div key={i} className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200">
                  <h3 className="text-white font-semibold mb-1">{item.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How to decide */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Πώς να <span className="text-pink-500">πάρεις την απόφαση</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-8">
              Η σωστή επιλογή ξεκινά από δύο ερωτήσεις: τι θέλεις να καταλήξεις να έχεις, και πόσο χρόνο/κόστος είσαι διατεθειμένος να βάλεις.
            </p>
            <div className="space-y-6">
              {[
                { step: '01', title: 'Αποφάσισε αν θέλεις νέο τατουάζ ή καθαρό δέρμα', desc: 'Αυτό ξεκαθαρίζει αμέσως αν cover up ή λέιζερ είναι η σωστή κατεύθυνση.' },
                { step: '02', title: 'Δες την κατάσταση του παλιού tattoo', desc: 'Χρώμα, πυκνότητα, ηλικία και μέγεθος καθορίζουν αν χρειάζεται fading πριν το cover up ή αν μπορείς να προχωρήσεις άμεσα.' },
                { step: '03', title: 'Κάνε consultation με έναν cover up artist', desc: 'Ένας artist που ειδικεύεται σε cover ups μπορεί να σου πει επί τόπου αν χρειάζεται fading και πόσο ρεαλιστικό είναι το αποτέλεσμα που θέλεις.' },
                { step: '04', title: 'Σκέψου το χρονοδιάγραμμα', desc: 'Αν έχεις βιασύνη, το cover up (χωρίς fading) είναι πιο γρήγορο. Αν έχεις υπομονή και θέλεις το καλύτερο δυνατό αποτέλεσμα, ίσως αξίζει ο συνδυασμός.' },
              ].map((item) => (
                <div key={item.step} className="flex gap-6 items-start">
                  <div className="text-4xl font-bold text-pink-600/30 shrink-0 w-12 text-center leading-none pt-1">{item.step}</div>
                  <div className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 flex-1">
                    <h3 className="text-white font-semibold text-lg mb-2">{item.title}</h3>
                    <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 210 approach */}
        <section className="py-16 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Πώς το αντιμετωπίζουμε <span className="text-pink-500">στο 210 Tattoo Athens</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-8">
              Δεν κάνουμε λέιζερ, αυτό είναι δουλειά ειδικών σε λέιζερ αφαίρεση. Αυτό που κάνουμε είναι cover ups και fading consultations. Αν χρειαστεί λέιζερ πριν, σε παραπέμπουμε, και σε βοηθάμε να κατανοήσεις πόσο fading χρειάζεσαι συγκεκριμένα για αυτό που θέλεις να πετύχεις.
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { title: 'Honest αξιολόγηση', desc: 'Σου λέμε αν το cover up μπορεί να γίνει άμεσα ή αν χρειάζεται fading πρώτα, χωρίς να υποσχόμαστε αδύνατα.' },
                { title: 'Design που δουλεύει με το παλιό', desc: 'Το σχέδιο του cover up δεν σχεδιάζεται παράλληλα με το παλιό tattoo, σχεδιάζεται γύρω από αυτό.' },
                { title: 'Παραπομπή αν χρειαστεί', desc: 'Αν χρειάζεσαι fading, μπορούμε να σε κατευθύνουμε σε αξιόπιστο κέντρο λέιζερ και να συντονιστούμε για το επόμενο βήμα.' },
                { title: 'Ένα αποτέλεσμα που θα σε ικανοποιεί', desc: 'Δεν κάνουμε cover ups για να «ξεφορτωθούμε» παλιά tattoos. Το τελικό αποτέλεσμα πρέπει να είναι κάτι που θα αγαπάς.' },
              ].map((item, i) => (
                <div key={i} className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200">
                  <h3 className="text-white font-semibold mb-1">{item.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Έχεις tattoo που δεν θέλεις πια και <span className="text-pink-500">δεν ξέρεις τι να κάνεις;</span></h2>
            <p className="text-gray-300 text-lg mb-8">
              Στείλε μας φωτογραφία στο Instagram και σου λέμε αμέσως αν μπορεί να γίνει cover up, αν χρειάζεται fading πρώτα και ποιες επιλογές υπάρχουν.
            </p>
            <a href="https://ig.me/m/210tattoo" target="_blank" rel="noopener noreferrer" className="bg-gradient-to-r from-pink-600 to-pink-700 hover:from-pink-700 hover:to-pink-800 px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-300 inline-flex items-center gap-3 shadow-2xl hover:shadow-pink-500/30 transform hover:scale-105">
              <Instagram size={20} />
              Στείλε Φωτογραφία
            </a>
          </div>
        </section>

        {/* Related articles */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-white mb-8">Σχετικά <span className="text-pink-500">Άρθρα</span></h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <Link to="/blog/tattoo-cover-up-athens" className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200 group">
                <p className="text-pink-500 text-xs uppercase tracking-widest mb-2">Άρθρο</p>
                <h3 className="text-white font-semibold group-hover:text-pink-400 transition-colors">Tattoo Cover Up στην Αθήνα</h3>
                <p className="text-gray-400 text-sm mt-2 leading-relaxed">Πότε μπορεί να καλυφθεί ένα παλιό tattoo και τι κάνει ένα cover up πετυχημένο.</p>
              </Link>
              <Link to="/blog/tattoo-artist-athens" className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200 group">
                <p className="text-pink-500 text-xs uppercase tracking-widest mb-2">Άρθρο</p>
                <h3 className="text-white font-semibold group-hover:text-pink-400 transition-colors">Πώς να Διαλέξεις Tattoo Artist</h3>
                <p className="text-gray-400 text-sm mt-2 leading-relaxed">Τι να προσέχεις πριν επιλέξεις studio, portfolio, υγιεινή και consultation.</p>
              </Link>
              <Link to="/blog/cover-up-i-laser-athina" className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200 group">
                <p className="text-pink-500 text-xs uppercase tracking-widest mb-2">Άρθρο</p>
                <h3 className="text-white font-semibold group-hover:text-pink-400 transition-colors">Cover Up ή Laser; Τι να Επιλέξεις</h3>
                <p className="text-gray-400 text-sm mt-2 leading-relaxed">Πότε υπερτερεί το καθένα και πώς ο συνδυασμός λύνει προβλήματα.</p>
              </Link>
            </div>
          </div>
        </section>

        <section className="py-16 bg-gray-950 border-t border-pink-600/10">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-white mb-8 text-center">Υπηρεσίες <span className="text-pink-500">/ Services</span></h2>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="/fine-line-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Fine Line Tattoo Athens</a>
              <a href="/realistic-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Realistic Tattoo Athens</a>
              <a href="/cover-up-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Cover Up Tattoo Athens</a>
              <a href="/black-gray-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Black &amp; Gray Tattoo Athens</a>
              <a href="/custom-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Custom Tattoo Athens</a>
              <a href="/blackwork-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Blackwork Tattoo Athens</a>
            </div>
          </div>
        </section>
      </BlogLayout>
    </>
  );
}
